import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import css from './CreativePopupTabs.css';
import { CreativeType } from '../../types';
import {
    CreativeCardTabs,
    ANALYSIS_TAB_ID,
    CREATIVE_TAGS_TAB_ID,
    CLUSTERED_CREATIVES_TAB_ID,
    TRACK_EVENT_PREFIX,
} from '../../consts';
import { Tabs } from '../../../components/widgets';
import { AutoCompleteOptionType } from '../../../components/molecules/autocompleteTypes';
import { trackMixpanelEvent } from '../../../utils/general';
import { GeneralPopup, PopupTypes } from '../../../components/partials';
import CreativeTagsTab from './CreativeTagsTab';
import AnalysisTab from './AnalysisTab';
import { useCreativeTags, useCreativeAnalysis, useSimilarCreativeAnalysis } from './chartDataHooks';
import { IconSizes } from '../../../components/foundations/Icon';
import SimilarCreativesTab from './SimilarCreativesTab';

const CreativePopupTabs = ({
    app,
    source,
    creative,
    selectedMetrics = [],
    selectedCohorts = [],
    dateRange,
    similarCreatives = [],
    onHasUnsavedChanges = () => {},
    hasUnsavedChanges = false,
    isLoadingMetrics = false,
    isLoadingCohorts = false,
    similarCreativesError = false,
    isLoadingSimilarCreatives = false,
}) => {
    const { imageHash, metrics = {}, cohorts = {} } = creative;
    const [activeTab, setActiveTab] = useState(ANALYSIS_TAB_ID);
    const [pendingTabId, setPendingTabId] = useState();

    const switchTab = tabId => {
        setActiveTab(tabId);
        trackMixpanelEvent(TRACK_EVENT_PREFIX, 'Pop Up: Tab Click', { tab: tabId });
    };

    const handleTabClick = tabId => {
        if (hasUnsavedChanges && tabId !== activeTab) {
            setPendingTabId(tabId);
        } else {
            switchTab(tabId);
        }
    };

    const handleConfirmTabSwitch = () => {
        switchTab(pendingTabId);
        closeTabSwitchConfirmation();
    };

    const closeTabSwitchConfirmation = () => {
        setPendingTabId();
    };

    const { loading: isLoadingCreativeTags, aiDimensions } = useCreativeTags({ app, imageHash });

    const chartData = useCreativeAnalysis({ app, source, imageHash, selectedMetrics, selectedCohorts, dateRange });

    const clusterCreatives = useMemo(() => isLoadingSimilarCreatives ? [] : [creative, ...similarCreatives], [creative, similarCreatives, isLoadingSimilarCreatives]);

    const similarCreativeAnalysis = useSimilarCreativeAnalysis({ app, source, clusterCreatives, dateRange });

    return (
        <div className={css.container}>
            <GeneralPopup
                open={!!pendingTabId}
                type={PopupTypes.INFO_WITH_CANCEL}
                title="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.TITLE"
                text="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.MESSAGE"
                acceptText="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.ACCEPT"
                rejectText="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.REJECT"
                onAccept={handleConfirmTabSwitch}
                onReject={closeTabSwitchConfirmation}
            />
            <Tabs containerClass={css.tabsContainer} selected={activeTab} onClick={handleTabClick} tabs={CreativeCardTabs} iconSize={IconSizes.MEDIUM} />
            {activeTab === CREATIVE_TAGS_TAB_ID && (
                <CreativeTagsTab
                    app={app}
                    source={source}
                    imageHash={imageHash}
                    onHasUnsavedChanges={onHasUnsavedChanges}
                    isLoading={isLoadingCreativeTags}
                    aiDimensions={aiDimensions}
                />
            )}
            {activeTab === ANALYSIS_TAB_ID && (
                <AnalysisTab
                    metrics={metrics}
                    cohorts={cohorts}
                    selectedMetrics={selectedMetrics}
                    selectedCohorts={selectedCohorts}
                    dateRange={dateRange}
                    isLoadingMetrics={isLoadingMetrics}
                    isLoadingCohorts={isLoadingCohorts}
                    chartData={chartData}
                />
            )}
            {activeTab === CLUSTERED_CREATIVES_TAB_ID && (
                <SimilarCreativesTab
                    similarCreatives={similarCreatives}
                    isLoadingSimilarCreatives={isLoadingSimilarCreatives}
                    similarCreativesError={similarCreativesError}
                    chartData={similarCreativeAnalysis}
                />
            )}
        </div>
    );
};

CreativePopupTabs.propTypes = {
    app: PropTypes.string.isRequired,
    source: PropTypes.string,
    creative: PropTypes.shape(CreativeType).isRequired,
    selectedMetrics: PropTypes.arrayOf(AutoCompleteOptionType),
    selectedCohorts: PropTypes.arrayOf(AutoCompleteOptionType),
    dateRange: PropTypes.object.isRequired,
    similarCreatives: PropTypes.arrayOf(PropTypes.shape(CreativeType)),
    onHasUnsavedChanges: PropTypes.func,
    hasUnsavedChanges: PropTypes.bool,
    isLoadingMetrics: PropTypes.bool,
    isLoadingCohorts: PropTypes.bool,
    isLoadingSimilarCreatives: PropTypes.bool,
    similarCreativesError: PropTypes.bool,
};

export default CreativePopupTabs;
